import { createPopper } from '@popperjs/core';
import { google, outlook, yahoo, ics } from 'calendar-link';

/**
 * Default popper options
 */
const defaultOptions = {
  placement: 'bottom',
  modifiers: [
    {
      name: 'offset',
      options: {
        // distantiate a bit from the ref element.
        offset: [0, 10]
      }
    }
  ]
}

class CalendarPopup {
  /**
   * Create a new instance of CalendarPopup for showing add to calendar links.
   *
   * @param {Element} el The reference element to which the calendar popup will be attached. Use a button.
   * @param {Element} popupEl The element containing the links to add to specific calendars
   * @param {object} options Optional. popperjs options
   */
  constructor(el, popupEl, options = null) {
    this.el = el;
    this.popupEl = popupEl;
    this.options = options || defaultOptions;
    this.isPopupVisible = false;

    // instantiate the popper instance
    this.popperInstance = createPopper(this.el, this.popupEl, this.options);

    // apply event listener for showing and hiding the popup el
    this.#generateAndPopulateLinks();
    this.#addEventListeners();
  }

  /* ----------------------------- Public ---------------------------- */
  /**
   * Hides the popup
   */
  hide() {
    this.isPopupVisible = false;
    this.popupEl.removeAttribute('data-show');

    // Disable the event listeners
    this.popperInstance.setOptions((options) => ({
      ...options,
      modifiers: [
        ...options.modifiers,
        { name: 'eventListeners', enabled: false },
      ]
    }));
  }

  /**
   * Makes the popup visible
   */
  show() {
    this.popupEl.setAttribute('data-show', '');

    // Enable the event listeners
    this.popperInstance.setOptions((options) => ({
      ...options,
      modifiers: [
        ...options.modifiers,
        { name: 'eventListeners', enabled: true },
      ]
    }));

    // Update it's position
    this.popperInstance.update();
    this.isPopupVisible = true;
  }


  /* ----------------------------- Private ---------------------------- */
  #generateAndPopulateLinks() {
    // Grab the event details
    const title = this.el.dataset.eventTitle;
    const description = this.el.dataset.eventDescription || '';
    const location = this.el.dataset.eventLocation;

    let start = this.el.dataset.eventStart;
    let end = this.el.dataset.eventEnd || '';

    if (start) {
      start = new Date(start);
    }
    if (end) {
      end = new Date(end);
    }

    const eventDetails = {
      title,
      description,
      start,
      end,
      location
    };

    // Attach 'add to calendar' links
    const calendarLinkTags = this.popupEl.querySelectorAll('a');
    calendarLinkTags.forEach(aTag => {
      if (aTag.dataset.type === "google") {
        aTag.href = google(eventDetails)
      } else if (aTag.dataset.type === "outlook") {
        aTag.href = outlook(eventDetails)
      } else if (aTag.dataset.type === "apple") {
        aTag.href = ics(eventDetails)
      } else if (aTag.dataset.type === "yahoo") {
        aTag.href = yahoo(eventDetails)
      }
    });
  }

  /**
   * Add show and hide behaviour on click and blur of {@link el} element
   */
  #addEventListeners() {
    this.el.addEventListener('click', () => {
      if (this.isPopupVisible) {
        this.hide();

      } else {
        this.show();
      }
    });

    this.el.addEventListener('blur', () => {
      setTimeout(() => {
        this.hide();
      }, 200);
    });
  }

}

export default CalendarPopup;
